import styled from 'styled-components';

import {
  grey1,
  grey5,
  grey7,
  grey9,
  purple1,
  purple5,
  purple7,
} from 'approot/shared/styles/colours.style';
import { rem, mediaHover } from 'approot/shared/style';
import {
  borderRadius8,
  baseBoxShadowFocus,
} from 'approot/shared/styles/borders.style';
import {
  FlexCentreAlign,
  flexCentreAlignCss,
} from 'approot/shared/styles/flex.style';
import {
  spacing12,
  spacing8,
  spacing16,
} from 'approot/shared/styles/spacing.style';
import { fontSize12, fontSize16 } from 'approot/shared/styles/typography.style';
import { ComponentPlusWrapper } from '../components/components.style';
import { ACTIVITY_BUILDER_DRAGGABLE_SURFACE_HANDLE_Z_INDEX } from 'approot/activity-builder/activity-builder.constants';

type DroppableProps = {
  $canDrop: boolean;
  $isOver: boolean;
};
export const Droppable = styled(FlexCentreAlign)<DroppableProps>`
  background-color: ${({ $isOver }) => ($isOver ? purple1 : grey1)};
  border-radius: ${rem(4)};
  border: 1px dashed ${({ $isOver }) => ($isOver ? purple5 : grey5)};
  color: ${({ $isOver }) => ($isOver ? purple7 : grey9)};
  font-size: ${rem(fontSize16)};
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
  visibility: ${({ $canDrop }) => ($canDrop ? 'visible' : 'hidden')};
  z-index: ${({ $isOver }) => ($isOver ? 2 : 0)};
`;

type DroppableWrapperProps = {
  $isDragging: boolean;
  $isFixedPosition?: boolean;
};
export const DroppableWrapper = styled.div<DroppableWrapperProps>`
  height: ${({ $isDragging }) => rem($isDragging ? 24 : 0)};
  overflow: hidden;
  position: relative;
  // this delay is important for react-dnd to stop cancelling a drag
  transition: height 100ms 100ms ease;

  ${({ $isDragging }) =>
    $isDragging &&
    `
    & + ${ComponentPlusWrapper} {
      height: 0;
      visibility: hidden;
      transition: height 100ms 100ms ease;
    }
  `}

  ${({ $isFixedPosition }) =>
    $isFixedPosition &&
    `
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    position: absolute;
    height: auto;

    ${Droppable} {
      height: 100%;
    }
`}
`;

export const DraggableSurfaceTooltipTarget = styled.span`
  background-color: ${grey9};
  border-radius: ${rem(borderRadius8)};
  color: ${grey1};
  display: none;
  font-size: ${rem(fontSize12)};
  padding: ${rem(spacing8)} ${rem(spacing16)};
`;

export const DraggableSurfaceHandle = styled.div`
  ${flexCentreAlignCss}
  border-radius: ${rem(borderRadius8)};
  border: 1px solid transparent;
  flex-wrap: wrap;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${ACTIVITY_BUILDER_DRAGGABLE_SURFACE_HANDLE_Z_INDEX};

  & > div {
    height: 100%;
    width: 100%;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.7);
    border: 1px solid ${grey5};
    cursor: grab;

    ${DraggableSurfaceTooltipTarget} {
      display: block;
    }
  }
`;

export const DraggableHandle = styled.div`
  background-color: ${grey1};
  border: 1px solid ${grey5};
  border-radius: ${rem(borderRadius8)};
  cursor: move;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(24)};
  position: absolute;
  margin-left: -${rem(spacing12)};
  top: -${rem(spacing12)};
  left: 50%;
  width: ${rem(24)};
  z-index: 2;
`;

type DraggableSectionProps = {
  $isDragging?: boolean;
  $isActive?: boolean;
  $isKeyboardFocussed?: boolean;
  $isColumn?: boolean;
};
export const Draggable = styled.div<DraggableSectionProps>`
  border-radius: ${rem(4)};
  position: relative;
  width: 100%;

  ${mediaHover`
    ${DraggableHandle} {
      display: none;
    }
    &:hover > {
      ${DraggableHandle} {
        display: flex;
      }
    }
  `}

  ${({ $isDragging }) =>
    $isDragging &&
    `
    opacity: 0.4;
  `}

   ${({ $isActive }) =>
    $isActive &&
    `
     outline: 1px solid ${grey7};
     outline-offset: 4px;
      & > ${DraggableHandle} {
        display: flex;
      }
  `}

  ${({ $isColumn }) =>
    $isColumn &&
    `
    padding: 0;
  `}

  &:focus {
    outline: 2px solid black;
    outline-offset: 4px;
  }

  ${({ $isKeyboardFocussed }) =>
    $isKeyboardFocussed &&
    `
      
      &:focus {
        box-shadow: 0px 120px 120px rgba(22, 34, 51, 0.08), 0px 64px 64px rgba(22, 34, 51, 0.12), 0px 32px 32px rgba(22, 34, 51, 0.04), 0px 24px 24px rgba(22, 34, 51, 0.04), 0px 4px 24px rgba(22, 34, 51, 0.04), 0px 4px 4px rgba(22, 34, 51, 0.04);
      }
      box-shadow: ${baseBoxShadowFocus}, 0px 120px 120px rgba(22, 34, 51, 0.08), 0px 64px 64px rgba(22, 34, 51, 0.12), 0px 32px 32px rgba(22, 34, 51, 0.04), 0px 24px 24px rgba(22, 34, 51, 0.04), 0px 4px 24px rgba(22, 34, 51, 0.04), 0px 4px 4px rgba(22, 34, 51, 0.04);
  `}
`;
